"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "How do I start my training program?",
    answer: "Getting started is simple. Choose your preferred coaching package, complete your initial fitness assessment, and we will schedule your onboarding call to align on your specific goals and lifestyle."
  }, {
    question: "Can I adjust my training schedule?",
    answer: "Flexibility is key to consistency. You can update your availability or reschedule sessions directly through your client portal. We work around your life to ensure you never miss a beat."
  }, {
    question: "What does the personalized coaching include?",
    answer: "Every program is built from scratch. You receive custom workout plans, nutritional guidance, form correction videos, and weekly check-ins to track your progress and adjust your intensity."
  }, {
    question: "How do you track my progress?",
    answer: "We use a data-driven approach. Through our app, we monitor your strength gains, recovery metrics, and body composition changes, ensuring every session moves you closer to your target."
  }, {
    question: "What if I have an injury or limitation?",
    answer: "Safety is our priority. We specialize in functional movement and can modify any exercise to accommodate injuries or physical limitations while still pushing you toward your performance goals."
  }, {
    question: "Do you offer virtual training sessions?",
    answer: "Yes. Our virtual coaching provides the same high-touch experience as in-person training, using real-time video feedback to ensure your technique is perfect, no matter where you are."
  }, {
    question: "Can I train with a partner?",
    answer: "Absolutely. We offer small group and partner training options. It’s a great way to stay motivated and share the cost of coaching while still receiving personalized attention."
  }, {
    question: "Is nutritional advice included?",
    answer: "Nutrition is the foundation of performance. We provide macro-based guidance and sustainable habit-building strategies to fuel your workouts and optimize your recovery without restrictive dieting."
  }];
  return <section data-section-id="189" id="common-questions" className="bg-background py-32 font-default">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              

              <h2 className="font-default text-foreground text-5xl md:text-[3rem] leading-[1.05] tracking-tight mb-8">
                Training,{" "}
                <span className="italic font-light">demystified.</span>
              </h2>

              <p className="font-default text-muted-foreground text-lg leading-relaxed max-w-md mb-10">
                Everything you need to know about working with Coach Kira. 
                From program structure to nutritional support, we are here to help.
              </p>

              <Link to="/StartYourJourneyPage" className="inline-flex items-center gap-2 font-default text-foreground text-base border-b border-foreground/40 pb-1 hover:border-foreground transition-colors duration-300">
                <span>Still have questions? Contact us</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group data-[state=open]:bg-accent/40 transition-colors duration-400 ease-out">
                  <AccordionTrigger className="hover:no-underline py-7 px-2 md:px-4 [&>svg]:hidden group/trigger">
                    <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.2em] text-chart-2 pt-2 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default text-foreground text-xl md:text-[1.25rem] leading-snug font-normal">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-5 shrink-0 text-foreground/60 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45 mt-2" strokeWidth={1.25} />
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 px-2 md:px-4">
                    <div className="flex gap-5 md:gap-8">
                      <span className="font-mono text-xs shrink-0 opacity-0 select-none">
                        00
                      </span>
                      <p className="font-default text-muted-foreground text-base md:text-[1.0625rem] leading-[1.75] max-w-2xl">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}